import { useCallback } from 'react';

import { SUPPORT_NOTIFIED_MESSAGE } from '@shared/i18n';
import { EBulkOperationStatus } from '@modules/items/types/bulkOperation.types';

import { AI_NO_ANSWER_FOUND_TITLE, AI_OUT_OF_SCOPE_TITLE } from '../constants';
import toAiErrorMessage from '../helpers/toAiErrorMessage.helper';
import useCanUseAiQa from '../hooks/useCanUseAiQa.hook';
import useAiAssistantStore from '../stores/useAiAssistantStore';
import type { TAiAssistantStatusMessage, TAiAssistantSurface, TAiAssistantOrigin } from '../types';
import { useGroundedQa } from './api';
import { EGroundedQaResultType, type TGroundedQaRunResponse } from './types';

type TParams = {
  surface: TAiAssistantSurface;
  origin: TAiAssistantOrigin | undefined;
};

const useAiAsk = ({ surface, origin }: TParams) => {
  const { read, update } = useAiAssistantStore();
  const { response, pendingRunId } = read(surface, origin);
  const setResponse = useCallback(
    (value: TGroundedQaRunResponse | null) => update(surface, origin, { response: value, submittedFeedback: null }),
    [origin, surface, update]
  );
  const setPendingRunId = useCallback(
    (value: string | null) => update(surface, origin, { pendingRunId: value }),
    [origin, surface, update]
  );
  const { submit, reset, cancel, canCancel, isPending, pendingRun, budgetRefusal, error } = useGroundedQa({
    setResponse,
    pendingRunId,
    setPendingRunId,
  });
  const canUse = useCanUseAiQa();
  const result = response?.result && response.resultType ? response : undefined;

  const statusMessage = ((): TAiAssistantStatusMessage | null => {
    if (error) return { text: toAiErrorMessage(error), intent: 'error' };
    if (!response || isPending) return null;
    if (response.status === EBulkOperationStatus.FAILED) {
      return { text: response.errorMessage || SUPPORT_NOTIFIED_MESSAGE, intent: 'error' };
    }
    if (response.result) return null;
    if (response.resultType === EGroundedQaResultType.OUT_OF_SCOPE) {
      return { text: AI_OUT_OF_SCOPE_TITLE, intent: 'warning' };
    }
    if (response.resultType === EGroundedQaResultType.NO_ANSWER_FOUND) {
      return { text: AI_NO_ANSWER_FOUND_TITLE, intent: 'warning' };
    }
    return null;
  })();

  return {
    canUse,
    isPending,
    isActive: isPending,
    result,
    submit,
    cancel,
    canCancel,
    clearResult: reset,
    pendingRun,
    budgetRefusal,
    statusMessage,
  };
};

export default useAiAsk;
